/**
 * SesongOvergang — DOM-overlay som markerer sesong-skift.
 *
 * Bygd i runde 43 for å gi sesongskift en filmatisk pause som
 * komplementerer toast-varselet og whoosh-partiklene fra runde 38.
 *
 * Effekt:
 * - Fullscreen mørk overlay tones inn over 200ms
 * - Stor sesong-tekst fader inn
 * - Holder i ~400ms
 * - Hele overlayet fader ut over 200ms
 *
 * Total varighet: ~800ms. Auto-cleanup.
 *
 * Respekterer reduced motion: ved redusert bevegelse skipper vi
 * animasjonen og viser bare en kort statisk overlay.
 */

import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import type { Sesong } from '@/systems/sesong';
import { SESONG_NAVN } from '@/systems/sesong';
import { voiceOver } from '@/systems/voiceOver';

const STYLE_ID = 'sp-sesong-overgang-style';
const ROOT_ID = 'sp-sesong-overgang';

const CSS = `
#${ROOT_ID} {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 10, 20, 0);
  pointer-events: none;
  z-index: 980;
  transition: background 200ms ease-out;
  font-family: var(--sp-font-display, 'Playfair Display', Georgia, serif);
}
#${ROOT_ID}.sp-aktiv {
  background: rgba(15, 10, 20, 0.78);
}
#${ROOT_ID} .sp-sesong-tekst {
  color: #f4ead5;
  font-size: clamp(36px, 6vw, 72px);
  font-weight: 700;
  text-align: center;
  opacity: 0;
  transform: translateY(20px) scale(0.96);
  transition: opacity 240ms ease-out, transform 240ms ease-out;
  text-shadow: 0 2px 30px rgba(184, 134, 11, 0.4);
  letter-spacing: 0.02em;
  padding: 0 24px;
}
#${ROOT_ID}.sp-aktiv .sp-sesong-tekst {
  opacity: 1;
  transform: translateY(0) scale(1);
}
#${ROOT_ID} .sp-sesong-undertekst {
  display: block;
  margin-top: 8px;
  font-size: clamp(16px, 2.5vw, 22px);
  font-family: var(--sp-font-body, 'DM Sans', system-ui, sans-serif);
  font-weight: 400;
  color: #d9a14b;
  font-style: italic;
}
@media (prefers-reduced-motion: reduce) {
  #${ROOT_ID}, #${ROOT_ID} .sp-sesong-tekst {
    transition: none;
  }
}
`;

const UNDERTEKST: Record<Sesong, string> = {
  vaar: 'forfatterne våkner',
  sommer: 'salget dipper, folk er på hytta',
  bokhost: 'power-vinduet åpner',
  jul: 'barnebok og feelgood selger gull',
  vinter: 'manus-tørke, ro før neste sesong',
};

type AktivOvergang = {
  root: HTMLDivElement;
  timer: ReturnType<typeof setTimeout>;
  fadeTimer: ReturnType<typeof setTimeout>;
};

export class SesongOvergang {
  private aktiv?: AktivOvergang;

  /**
   * Vis overgangs-overlay for en sesong. Hvis en overgang allerede
   * spiller, avsluttes den umiddelbart slik at den nye kan starte.
   */
  vis(sesong: Sesong): void {
    if (typeof document === 'undefined') return;

    this.sikreStyle();
    this.lukkUmiddelbart();

    const root = document.createElement('div');
    root.id = ROOT_ID;
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');

    const tekst = document.createElement('div');
    tekst.className = 'sp-sesong-tekst';
    const sesongNavn = SESONG_NAVN[sesong];
    tekst.textContent = sesongNavn;

    const undertekst = document.createElement('span');
    undertekst.className = 'sp-sesong-undertekst';
    undertekst.textContent = UNDERTEKST[sesong];
    tekst.appendChild(undertekst);

    root.appendChild(tekst);

    const host = document.getElementById('sp-canvas-host') ?? document.body;
    host.appendChild(root);

    const redusert = foretrekkerRedusertBevegelse();

    // Trigger transitions ved å legge til klasse på neste frame
    if (redusert) {
      root.classList.add('sp-aktiv');
    } else {
      requestAnimationFrame(() => {
        if (root.parentNode) root.classList.add('sp-aktiv');
      });
    }

    // Valgfri voice-over for sesongskift med narrator-stemme
    voiceOver.les(`${sesongNavn} er her`, 'narrator');

    const totalVarighet = redusert ? 600 : 800;
    const fadeUtStart = redusert ? 400 : 600;

    const fadeTimer = setTimeout(() => {
      root.classList.remove('sp-aktiv');
    }, fadeUtStart);

    const timer = setTimeout(() => {
      this.lukkUmiddelbart();
    }, totalVarighet);

    this.aktiv = { root, timer, fadeTimer };
  }

  /** Stopp aktiv overgang umiddelbart (uten fade). */
  lukkUmiddelbart(): void {
    if (!this.aktiv) return;
    clearTimeout(this.aktiv.timer);
    clearTimeout(this.aktiv.fadeTimer);
    if (this.aktiv.root.parentNode) {
      this.aktiv.root.parentNode.removeChild(this.aktiv.root);
    }
    this.aktiv = undefined;
  }

  /** For testing — er en overgang aktiv? */
  get erAktiv(): boolean {
    return !!this.aktiv;
  }

  private sikreStyle(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
}

export const sesongOvergang = new SesongOvergang();
